import { useEffect, useState } from "react";
import { assembleZip } from "../lib/zip";
import type { ZipInput } from "../lib/zip";
import type { VerificationResult } from "./RecognitionTest";
import { FIELD_CAPTURE_DEFAULT_HOLD_MS } from "../constants";

interface Props {
  zipInput: ZipInput;
  verificationResult?: VerificationResult | null;
  notes: string;
  onNotesChange(notes: string): void;
  onDone(): void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function ZipExport({ zipInput, verificationResult, notes, onNotesChange, onDone }: Props) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Notes are excluded here — included at download time only, so the zip
    // does not rebuild on every keystroke while the operator is typing.
    assembleZip(zipInput)
      .then((b) => {
        if (!cancelled) setBlob(b);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [zipInput]);

  async function getFinalBlob(): Promise<Blob> {
    if (notes.trim()) return assembleZip({ ...zipInput, notes });
    return blob!;
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const finalBlob = await getFinalBlob();
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${zipInput.poiId}-capture.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  async function handleShare() {
    setDownloading(true);
    try {
      const finalBlob = await getFinalBlob();
      const file = new File([finalBlob], `${zipInput.poiId}-capture.zip`, { type: "application/zip" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          return;
        } catch {
          // fall through to download
        }
      }
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${zipInput.poiId}-capture.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const containerStyle: React.CSSProperties = {
    padding: "1rem",
    maxWidth: 480,
    margin: "0 auto",
  };

  if (error) {
    return (
      <div style={{ ...containerStyle, textAlign: "center" }}>
        <p style={{ color: "#c0392b" }}>Failed to build zip: {error}</p>
        <button onClick={onDone} style={{ marginTop: "1rem" }}>
          Start Over
        </button>
      </div>
    );
  }

  if (!blob) {
    return (
      <div style={{ ...containerStyle, textAlign: "center" }}>
        <p>Building zip archive...</p>
      </div>
    );
  }

  const markerCount = zipInput.photos.length;

  const btnStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: "0.75rem",
    fontSize: "1rem",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    marginBottom: "0.75rem",
  };

  return (
    <div style={containerStyle}>
      <h2 style={{ marginBottom: "0.5rem" }}>Export Ready</h2>

      {/* Summary section */}
      <div style={summaryStyle.box}>
        <h3 style={summaryStyle.heading}>Capture Summary</h3>
        <div style={summaryStyle.list}>
          <div style={summaryStyle.row}>
            <span style={summaryStyle.label}>POI ID</span>
            <span style={summaryStyle.value}>{zipInput.poiId}</span>
          </div>
          <div style={summaryStyle.row}>
            <span style={summaryStyle.label}>GPS</span>
            <span style={summaryStyle.value}>
              {zipInput.gps.lat.toFixed(6)}, {zipInput.gps.lng.toFixed(6)}
              <br />
              <span style={summaryStyle.meta}>
                ±{Math.round(zipInput.gps.accuracyM)} m accuracy, {zipInput.gps.detectionRadiusM} m detection radius
              </span>
            </span>
          </div>
          <div style={summaryStyle.row}>
            <span style={summaryStyle.label}>Photos</span>
            <span style={summaryStyle.value}>
              1 hero photo, 1 mission photo, {markerCount} marker photo{markerCount !== 1 ? "s" : ""}
            </span>
          </div>
          <div style={summaryStyle.row}>
            <span style={summaryStyle.label}>Recognition</span>
            <span style={summaryStyle.value}>
              Threshold: {(zipInput.threshold ?? 0.65).toFixed(2)}, Hold: {zipInput.holdMs ?? FIELD_CAPTURE_DEFAULT_HOLD_MS} ms
            </span>
          </div>
          {zipInput.backendInfo && (
            <div style={summaryStyle.row}>
              <span style={summaryStyle.label}>Backend</span>
              <span style={summaryStyle.value}>
                <span style={{
                  display: "inline-block",
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "monospace",
                  background: (zipInput.backendInfo.maliOverride || zipInput.backendInfo.iosOverride) ? "#fff3cd" : "#d4edda",
                  color: (zipInput.backendInfo.maliOverride || zipInput.backendInfo.iosOverride) ? "#856404" : "#155724",
                  border: `1px solid ${(zipInput.backendInfo.maliOverride || zipInput.backendInfo.iosOverride) ? "#ffc107" : "#28a745"}`,
                  marginRight: 6,
                }}>
                  {zipInput.backendInfo.backend.toUpperCase()}
                </span>
                {zipInput.backendInfo.gpu ?? "GPU unknown"}
                {zipInput.backendInfo.maliOverride && (
                  <span style={{ ...summaryStyle.meta, display: "block" }}>
                    Mali detected — WASM backend used for correct embeddings
                  </span>
                )}
                {zipInput.backendInfo.iosOverride && (
                  <span style={{ ...summaryStyle.meta, display: "block" }}>
                    iOS detected — WASM backend used to avoid GPU memory pressure
                  </span>
                )}
              </span>
            </div>
          )}
          {verificationResult && (
            <div style={summaryStyle.row}>
              <span style={summaryStyle.label}>Verification</span>
              <span style={summaryStyle.value}>
                <span style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#fff",
                  background: verificationResult.passed ? "#2d7a2d" : "#c0392b",
                  marginRight: 8,
                }}>
                  {verificationResult.passed ? "PASS" : "FAIL"}
                </span>
                peak similarity {verificationResult.peakSimilarity.toFixed(3)}
              </span>
            </div>
          )}
        </div>
        <p style={summaryStyle.description}>
          This will create a zip file containing your hero photo, mission photo, and the recognition marker settings.
        </p>
      </div>

      {/* Notes for Kye Li */}
      <div style={notesStyle.section}>
        <label htmlFor="notes-for-kye-li" style={notesStyle.label}>
          Notes for Kye Li
        </label>
        <textarea
          id="notes-for-kye-li"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Mission context, reference details, special instructions for Kye Li..."
          rows={4}
          style={notesStyle.textarea}
        />
      </div>

      <p style={{ color: "#555", marginBottom: "1.5rem", textAlign: "center" }}>
        {zipInput.poiId} — {formatBytes(blob.size)}
      </p>

      <button
        onClick={() => void handleDownload()}
        disabled={downloading}
        style={{ ...btnStyle, background: downloading ? "#6a9fd4" : "#0057b7", color: "#fff", cursor: downloading ? "wait" : "pointer" }}
      >
        {downloading ? "Preparing…" : "Download"}
      </button>

      <button
        onClick={() => void handleShare()}
        disabled={downloading}
        style={{ ...btnStyle, background: downloading ? "#7ec89a" : "#27ae60", color: "#fff", cursor: downloading ? "wait" : "pointer" }}
      >
        {downloading ? "Preparing…" : "Share"}
      </button>

      <button
        onClick={onDone}
        disabled={downloading}
        style={{ ...btnStyle, background: "#ecf0f1", color: "#333" }}
      >
        Done
      </button>
    </div>
  );
}

const notesStyle: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: "1rem",
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    marginBottom: 6,
  },
  textarea: {
    display: "block",
    width: "100%",
    padding: "0.6rem 0.75rem",
    fontSize: 14,
    lineHeight: 1.5,
    color: "#222",
    background: "#fff",
    border: "1px solid #ccc",
    borderRadius: 6,
    resize: "vertical",
    boxSizing: "border-box",
  },
};

const summaryStyle: Record<string, React.CSSProperties> = {
  box: {
    background: "#f9f9f9",
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: "1rem",
  },
  heading: {
    fontSize: 15,
    fontWeight: 700,
    color: "#111",
    marginTop: 0,
    marginBottom: 10,
  },
  list: {
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "flex",
    gap: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b6b6b",
    minWidth: 90,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    margin: 0,
  },
  value: {
    fontSize: 13,
    color: "#222",
    margin: 0,
    flex: 1,
  },
  meta: {
    fontSize: 11,
    color: "#888",
  },
  description: {
    fontSize: 12,
    color: "#666",
    marginTop: 12,
    marginBottom: 0,
    lineHeight: 1.5,
  },
};
